/**
 * ===========================================
 * StudySpace ERP Frontend - Axios Configuration
 * ===========================================
 * 
 * Configures Axios instance with base URL and interceptors.
 * Handles authentication tokens and error responses.
 * 
 * Features:
 * - Automatic token attachment to requests
 * - Response error handling
 * - 401 handling for expired tokens
 * - Toast notifications for errors
 * 
 * @file src/api/axios.js
 * @description Axios instance with interceptors
 */

import axios from 'axios';
import toast from 'react-hot-toast';
import { STORAGE_KEYS } from '../utils/constants';

/**
 * Normalize the configured API URL.
 *
 * - Trims whitespace and trailing slashes.
 * - Appends `/api` when only a backend origin is supplied.
 * - Allows a relative path such as `/api` for same-origin proxies.
 *
 * @param {string} value - Raw VITE_API_URL value
 * @returns {string} Normalized URL, or an empty string when invalid
 */
const normalizeApiUrl = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmed = value.trim().replace(/\/+$/, '');
  if (!trimmed) {
    return '';
  }

  try {
    const parsed = new URL(trimmed);
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return '';
    }

    const path = parsed.pathname.replace(/\/+$/, '');
    return path ? `${parsed.origin}${path}` : `${parsed.origin}/api`;
  } catch {
    return trimmed.startsWith('/') ? trimmed : '';
  }
};

/**
 * API base URL from environment or development default.
 * Production builds are validated by scripts/validate-env.mjs; the guard
 * below prevents an accidental silent fallback to localhost if it is skipped.
 *
 * @type {string}
 */
const API_URL = normalizeApiUrl(import.meta.env.VITE_API_URL)
  || (import.meta.env.DEV ? 'http://localhost:5000/api' : '');

if (!API_URL && import.meta.env.PROD) {
  throw new Error(
    'VITE_API_URL is not configured. Set it to the deployed backend API URL before building.'
  );
}

/**
 * Create Axios instance with custom configuration
 * 
 * @constant {AxiosInstance}
 */
const axiosInstance = axios.create({
  // Base URL for all requests
  baseURL: API_URL,

  // Request timeout (30 seconds)
  timeout: 30000,

  // Default headers
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Request Interceptor
 * 
 * Automatically attaches JWT token to request headers
 * if the user is authenticated.
 */
axiosInstance.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);

    // If token exists, attach to Authorization header
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    // Log request error in development
    if (import.meta.env.DEV) {
      console.error('Request Error:', error);
    }
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * 
 * Handles successful responses and errors globally.
 * - 401 errors: Clear token and redirect to login
 * - 403 errors: Show access denied message
 * - 500 errors: Show generic error message
 * - Network errors: Show connection error message
 */
axiosInstance.interceptors.response.use(
  // Successful response handler
  (response) => {
    // Global success toast for non-GET requests
    const method = response.config?.method?.toLowerCase();
    const shouldToastSuccess =
      method &&
      method !== 'get' &&
      response.data?.message &&
      response.config?.showToast !== false;

    if (shouldToastSuccess) {
      toast.success(response.data.message);
    }

    return response;
  },

  // Error response handler
  (error) => {
    // Handle network errors (no response)
    if (!error.response) {
      toast.error('Network error. Please check your connection.');
      return Promise.reject(error);
    }

    const { status, data } = error.response;

    // Handle different error status codes
    switch (status) {
      case 401:
        // Unauthorized - token expired or invalid
        // Clear stored auth data
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER);

        // Show toast message
        toast.error(data?.message || 'Session expired. Please log in again.');

        // Redirect to login (if not already there)
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        break;

      case 403:
        // Forbidden - no permission
        toast.error(data?.message || 'You do not have permission to perform this action.');

        // Redirect to unauthorized page for consistent handling
        if (window.location.pathname !== '/unauthorized') {
          window.location.href = '/unauthorized';
        }
        break;

      case 404:
        // Not found - resource doesn't exist
        // Don't show toast for 404s, let the component handle it
        break;

      case 409:
        // Conflict - duplicate resource
        toast.error(data?.message || 'This resource already exists.');
        break;

      case 422:
        // Unprocessable - business logic error
        toast.error(data?.message || 'Unable to process your request.');
        break;

      case 500:
      case 502:
      case 503:
        // Server errors
        toast.error('Server error. Please try again later.');
        break;

      default:
        // Generic error
        if (data?.message) {
          toast.error(data.message);
        }
    }

    return Promise.reject(error);
  }
);

/**
 * Export the configured Axios instance
 * 
 * @example
 * import api from './api/axios';
 * 
 * // GET request
 * const response = await api.get('/users');
 * 
 * // POST request
 * const response = await api.post('/sessions/checkin', { roomId, userId });
 */
export default axiosInstance;

/**
 * Helper function to handle API errors consistently
 * 
 * @function handleApiError
 * @param {Error} error - Axios error object
 * @returns {string} Error message for display
 * 
 * @example
 * try {
 *   await api.post('/login', credentials);
 * } catch (error) {
 *   const message = handleApiError(error);
 *   setError(message);
 * }
 */
export const handleApiError = (error) => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.response?.data?.errors) {
    // Return first validation error
    const firstError = Object.values(error.response.data.errors)[0];
    return firstError;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};
